/**
 * Grading a GPS fix.
 *
 * "An arrow that occasionally admits 'I'm fuzzy right now' is trusted; an arrow that confidently
 * lies once is deleted" (`ProductOverview.md` §1). These are the thresholds that decide which
 * of those the interface is being.
 */

import { CONFIDENCE } from './config';

/** How much to trust a fix, from its reported accuracy. */
export type Confidence = 'good' | 'okay' | 'poor' | 'unusable';

export function confidenceFor(accuracyMetres: number | null | undefined): Confidence {
  if (accuracyMetres === null || accuracyMetres === undefined || !Number.isFinite(accuracyMetres)) {
    return 'unusable';
  }
  if (accuracyMetres > CONFIDENCE.rejectMetres) return 'unusable';
  if (accuracyMetres <= CONFIDENCE.goodMetres) return 'good';
  if (accuracyMetres <= CONFIDENCE.okayMetres) return 'okay';
  return 'poor';
}

/**
 * Whether a fix may be used to compute a bearing at all.
 *
 * Fixes worse than a hundred metres are excluded from the arrow entirely, while the status line
 * still explains what is happening (`ProductOverview.md` §4.6).
 */
export const isUsableFix = (accuracyMetres: number | null | undefined): boolean =>
  confidenceFor(accuracyMetres) !== 'unusable';

/** The words the interface uses. Short, because they sit under a big number. */
export const CONFIDENCE_LABEL: Record<Confidence, string> = {
  good: 'good signal',
  okay: 'okay signal',
  poor: 'poor signal',
  unusable: 'no usable signal',
};
